import React, { Component } from 'react';
import multiKeyNameConflict from 'app/forms/config/validators/multiKeyNameConflict';
import { inject, observer } from 'mobx-react';
import { CREDENTIAL_TYPE_TEMPLATES } from '@kentik/ui-shared/credentials/constants';
import { Field, FormDialog, InputGroup, InputMultiple, PasswordInput, Select, SingleLineErrors } from 'core/form';
import { Box, Card, ConfirmDialog, Flex, Link, Text } from 'core/components';
import LabelSelector from 'app/components/labels/LabelSelector';
import CredentialTypeOptionRenderer from 'app/views/settings/credentials/components/CredentialTypeOptionRenderer';
import { addMetadataToOptions, buildOptionsFromMap } from 'core/form/components/modalSelect/selectHelpers';

export const fields = {
  name: {
    label: 'Name',
    rules: 'required|alpha_dash',
    transform: {
      out: (value) => value.trim()
    },
    messages: {
      not_in: 'Name must be unique'
    }
  },
  description: {
    label: 'Description'
  },
  type: {
    label: 'Type',
    rules: 'required',
    defaultValue: 'ai_api_token',
    options: addMetadataToOptions(buildOptionsFromMap({ ai_api_token: 'AI API Token' }), CREDENTIAL_TYPE_TEMPLATES)
  },
  tokens: {
    isComplexArray: true
  },
  'tokens[].key': {
    label: 'Key',
    rules: `required|alpha_dash|${multiKeyNameConflict}`
  },
  'tokens[].value': {
    label: 'Value',
    rules: 'required', // since only fields required for provider will be displayed
    placeholder: '************'
  },
  labels: {
    label: 'Labels',
    defaultValue: []
  }
};

const Confirm = ({ deviceCount = 0, ...props }) => (
  <ConfirmDialog
    {...props}
    title="Credential has associated devices"
    confirmButtonText="Confirm"
    text={`Are you sure you want to change the type of this credential? It is being used by ${deviceCount} device${
      deviceCount > 1 ? 's' : ''
    }.`}
  />
);

@inject('$labels')
@observer
class CreateAiCredentialDialog extends Component {
  state = {
    confirmDialogOpen: false,
    isLoading: false,
    storedValues: undefined
  };

  form;

  componentDidMount() {
    this.applyTemplate();
  }

  finalizeSave = async (form, values) => {
    const { $labels, model, onClose, onSave } = this.props;
    this.setState({ isLoading: true, confirmDialogOpen: false });

    try {
      await model.save(values);
      this.setState({ isLoading: false });
      await Promise.all([model.collection.fetch({ force: true }), $labels.labels.fetch({ force: true })]);

      onClose();
    } finally {
      onSave();
      this.setState({ isLoading: false });
    }
  };

  handleSave = async (form, values) => {
    const { model } = this.props;

    if (model.deviceConsumers.length) {
      this.setState({ confirmDialogOpen: true, storedValues: values });
      // Saving is not completed at this stage
      return Promise.reject();
    }
    return this.finalizeSave(form, values);
  };

  applyTemplate = () => {
    const { form } = this;
    const template = CREDENTIAL_TYPE_TEMPLATES.ai_api_token;
    const tokensField = form.getField('tokens');

    tokensField.setValue([]);

    template.forEach(({ key }) => {
      tokensField.add({ key, value: '' });
    });
  };

  render() {
    const { model, onClose } = this.props;
    const { confirmDialogOpen, isLoading, storedValues } = this.state;

    const nameRules = `${fields.name.rules}|not_in:${model.collection.map((cred) => cred.get('name'))}`;

    return (
      <FormDialog
        entityName="Credential"
        fields={fields}
        options={{ name: 'Credentials Dialog Form', showPristineErrors: false }}
        model={model}
        onClose={onClose}
        width={700}
        formComponentProps={{
          onLoad: (form) => (this.form = form)
        }}
        formActionsProps={{
          onSubmit: this.handleSave,
          onCancel: onClose,
          forceLoading: isLoading
        }}
        isOpen
      >
        <Confirm
          onConfirm={() => this.finalizeSave(this.form, storedValues)}
          onCancel={() => this.setState({ confirmDialogOpen: false })}
          isOpen={confirmDialogOpen}
          deviceCount={model.deviceConsumers.length}
        />
        <Field name="name" autoFocus fill large rules={nameRules} disabled={!model.isNew}>
          <InputGroup />
        </Field>
        <Field name="description" fill large>
          <InputGroup />
        </Field>
        <Flex gap={1} alignItems="center" mb={2}>
          <Field name="type" large m={0} disabled>
            <Select menuWidth={350} optionRenderer={CredentialTypeOptionRenderer} />
          </Field>
          <Box flex={1}>
            <LabelSelector fieldName="labels" fill m={0} />
          </Box>
        </Flex>

        <Card p={2}>
          <Field name="tokens">
            {({ field: tokensField }) => (
              <>
                <Flex flexDirection="column" gap="12px">
                  <InputMultiple field={tokensField} addButtonText="Add">
                    {({ field }) =>
                      field.map((f, index) => (
                        <>
                          <Flex alignItems="center" key={f.key._id} gap="12px">
                            <Field
                              field={f.key}
                              disabled
                              showError={false}
                              showLabel={index === 0}
                              large
                              width={200}
                              m={0}
                            >
                              <InputGroup />
                            </Field>
                            <Field field={f.value} large flex={1} m={0} showError={false} showLabel={index === 0} fill>
                              <PasswordInput showVisibleToggle={f.value.dirty} autoComplete="new-password" />
                            </Field>
                          </Flex>
                          <SingleLineErrors fields={[f.key, f.value]} inline />
                        </>
                      ))
                    }
                  </InputMultiple>
                </Flex>
              </>
            )}
          </Field>
        </Card>
        <Flex mt={1}>
          <Text muted>
            To edit existing credentials, navigate to{' '}
            <Link fontWeight="bold" to="/v4/settings/credentials">
              Credentials Vault
            </Link>
            .
          </Text>
        </Flex>
      </FormDialog>
    );
  }
}

export default CreateAiCredentialDialog;
